import { DragDropModule } from '@angular/cdk/drag-drop';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  animationFrameScheduler,
  BehaviorSubject,
  delayWhen,
  distinctUntilChanged,
  map,
  Observable,
  observeOn,
  startWith,
  Subject,
} from 'rxjs';

import { BlazeAccountFacade } from '@blaze-shared/app-state-data-access';
import {
  type ElementFormState,
  getActiveBinding,
  ImageRatio,
  InputElementMetaUiFeatures,
  makeBorderStyles,
  makeContainerVisualEffectStyles,
  makeImageAlignmentStyles,
} from '@blaze-shared/elements-domain';
import {
  DatasetColumn,
  getColumnKey,
  getDisplayStringFromLinkValue,
  isDatasetLinkValue,
} from '@blaze-shared/schema-domain';
import { ImageDialogComponent, safeDetectChanges } from '@blaze-shared/shared-ui';
import { countedShareReplay, toInt, toRatio, useEmitTracker } from '@blaze-shared/shared-util';
import { FileResult, UploaderFacade, UploaderOptions } from '@blaze-shared/uploader';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { ElementLabelComponent } from '../../components/element-label/element-label.component';
import { ElementLinkService } from '../../services/element-link.service';
import { FileService } from '../../services/file.service';
import { CanvasElementBaseComponent } from '../__base/base.component';

import {
  DEFAULT_IMAGE_URI,
  EMPTY_ICON_MAP,
  ImageClickAction,
  ImageEmptyThumbnail,
  PLACEHOLDERS_PATH,
} from './image.models';

// TODO: test
@UntilDestroy()
@Component({
  selector: 'be-image',
  imports: [AsyncPipe, DragDropModule, ElementLabelComponent, MatIconModule],
  templateUrl: './image.component.html',
  styleUrls: ['./image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageComponent
  extends CanvasElementBaseComponent<'image'>
  implements CanvasElementComponent<'image'>, OnChanges, OnInit
{
  private readonly blazeAccountFacade = inject(BlazeAccountFacade);
  private readonly dialog = inject(MatDialog);
  private readonly elementLinkService = inject(ElementLinkService);
  private readonly fileService = inject(FileService);
  private readonly uploader = inject(UploaderFacade);

  @Input() value?: string | string[];
  @Input() formState?: ElementFormState;

  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  private readonly outputEmitTracker = useEmitTracker();
  private readonly dataValue = this.getDataInput('value');
  private readonly triggerUpdate$ = new Subject<typeof this.value>();

  private wasEmitted = false;
  private elementHeightFixed?: boolean;
  private elementHeightValue?: number;
  private valueField?: string;

  readonly acceptedExtensions: string = this.getSupportedFileTypes().join(', ');
  readonly supportedExtensions: string = this.getSupportedFileTypes()
    .map((ext) => ext.replace('.', ''))
    .join(', ');

  labelEnabled!: boolean;
  labelText!: string;
  isReadOnly!: boolean;
  allowEdit = false;
  forceMaxWidth?: string;
  forceMaxHeight?: string;
  forceRatio?: ImageRatio;
  forceRatioCustom?: string;

  sectionStyles!: Record<string, string>;
  imageStyles!: Record<string, string>;
  imagePlaceholderStyles!: Record<string, string>;
  imageUploadZoneStyles!: Record<string, string>;
  imagePaginationStyles!: Record<string, string>;
  validationStyles!: Record<string, string>;

  draggingOver = false;
  lastImageSelected?: FileResult;

  currentImageDisplayIndex = 0;
  currentImageDisplayIndex$: Observable<number>;
  scrollData$$ = new BehaviorSubject<{ left: number; width: number }>({ left: 0, width: 1 });

  get hasValidationError(): boolean {
    return !!this.formState?.errors?.required || !!this.formState?.errors?.invalid;
  }

  get isClickActionAllowed(): boolean {
    const { action, navigateUrl, navigatePageId } = this.props?.content.link || {};
    const isNavigateToUrl = action === 'Url' && !!navigateUrl;
    const isNavigateToPage = action === 'GoToPage' && !!navigatePageId;
    return (!this.isStaticImageSource && !!this.clickAction) || isNavigateToUrl || isNavigateToPage;
  }

  get clickAction(): ImageClickAction | undefined {
    return this.props?.content.options?.clickAction;
  }

  get uploadLimit(): number {
    return this.props?.content.options?.unlimitedUpload && this.isImageBound
      ? Number.MAX_SAFE_INTEGER
      : this.props?.content.options?.uploadMax || 1;
  }

  get addButtonVisible(): boolean {
    return (
      !this.isReadOnly &&
      (!this.value?.length || this.value.length < this.uploadLimit) &&
      this.isImageBound
    );
  }

  get invisiblePagination(): boolean {
    return this.props?.content.options?.behavior === 'contain';
  }

  get isStaticImageSource(): boolean {
    return !!this.props?.content?.imageSource?.enabled && !this.isImageBound;
  }

  get isImageBound(): boolean {
    const activeBinding = getActiveBinding(this.props?.data);
    return !!activeBinding?.enabled && !!activeBinding?.dataSource;
  }

  get hasCustomPlaceholder(): boolean {
    return !!this.props?.content.imageSource.placeholder?.fileName;
  }

  get placeholderImageUri(): string {
    const fileName = this.props?.content.imageSource.placeholder?.fileName ?? '';
    return fileName ? `${PLACEHOLDERS_PATH}/${fileName}` : DEFAULT_IMAGE_URI;
  }

  get showUploader(): boolean {
    return !this.isStaticImageSource && !this.value?.length && !this.lastImageSelected;
  }

  get imageUrls(): string[] {
    if (this.isStaticImageSource) {
      return [this.props?.content?.imageSource?.url || this.placeholderImageUri];
    } else {
      const imageValue = this.valueField
        ? (value: any): any => this.valueField && value?.[this.valueField]
        : (value: any): any => value;
      return Array.isArray(this.value)
        ? this.value.map(imageValue)
        : [imageValue(this.value) || this.lastImageSelected?.url || ''];
    }
  }

  constructor() {
    super();

    this.currentImageDisplayIndex$ = this.scrollData$$.pipe(
      observeOn(animationFrameScheduler),
      map((scroll) => {
        return Math.round((scroll.left * (this.value?.length ?? 1)) / scroll.width);
      }),
      startWith(0),
      countedShareReplay(),
    );
    this.element$
      .pipe(
        map((el) => ({ fixed: el.height?.fixed, value: el.height?.value })),
        distinctUntilChanged((a, b) => a.fixed === b.fixed && a.value === b.value),
        untilDestroyed(this),
      )
      .subscribe(({ fixed, value }) => {
        this.elementHeightFixed = fixed;
        this.elementHeightValue = value;
        this.updateStyles();
      });

    this.triggerUpdate$
      .pipe(
        delayWhen(() => this.getElementEventSchemaSet('change')),
        untilDestroyed(this),
      )
      .subscribe((value) => {
        this.updateOutput(value || '');
      });

    this.getDataInput('value')
      .schema$.pipe(untilDestroyed(this))
      .subscribe(() => {
        this.setDefaultEventSchema('change');
      });

    this.initMarginHandler((props) => props?.style?.image?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['props']) {
      this.updateValueField();
      if (!this.bindingResults?.['value'] && this.value) {
        this.triggerUpdate$.next(this.value);
      }
      const { title, options } = this.props?.content || {};
      this.labelEnabled = title?.enabled ?? false;

      this.allowEdit = options?.allowEdit ?? false;
      this.forceMaxWidth = options?.forceMaxWidth;
      this.forceMaxHeight = options?.forceMaxHeight;
      this.forceRatio = options?.forceRatio;
      this.forceRatioCustom = options?.forceRatioCustom;
      this.updateStyles();
    }

    if (changes?.['value']) {
      if (Array.isArray(this.value) && this.value.every((val) => isDatasetLinkValue(val))) {
        this.value = this.value.map(
          (val) => (isDatasetLinkValue(val) && getDisplayStringFromLinkValue(val.display)) || val,
        );
      } else if (isDatasetLinkValue(this.value)) {
        getDisplayStringFromLinkValue(this.value.display);
      }
      if (!this.value || (Array.isArray(this.value) && !this.value.length)) {
        this.lastImageSelected = undefined;
      }
      this.triggerUpdate$.next(this.value);
      this.updateStyles();
    }
  }

  ngOnInit(): void {
    this.getElementUiFeatures<InputElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe((features) => {
        this.labelText = (features.title ?? '') + this.getRequiredSuffix(features);
        this.isReadOnly = features.isReadOnly || this.props?.content?.options?.readonly || false;

        safeDetectChanges(this.cdr);
      });
    this.initOutputSchema();

    if (this.bindingResults?.['value']) {
      this.triggerUpdate$.next(this.value);
    } else {
      this.setDefaultEventSchema('change');
    }
  }

  updateListScroll(event: Event): void {
    const viewport = event.target as HTMLElement;
    this.scrollData$$.next({
      left: viewport.scrollLeft,
      width: viewport.scrollWidth,
    });
  }

  allowDrop(event: DragEvent): void {
    if (this.isReadOnly) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }

    this.draggingOver = true;
  }

  drop(event: DragEvent): void {
    if (this.isReadOnly) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();

    const files = event.dataTransfer?.files ? Array.from(event.dataTransfer?.files) : [];

    this.upload(files);
  }

  emitClick(toolbarTriggered = false, index: number): void {
    if (this.isClickActionAllowed) {
      if (this.clickAction === 'download') {
        this.download(index);
        return;
      } else if (
        this.clickAction === 'expand' ||
        (!this.isReadOnly && !this.props?.content.link?.action)
      ) {
        this.expand(index);
        return;
      }
    }

    if (this.isInEditor && !toolbarTriggered) {
      // Ignore if click was not triggered by the toolbar action in the editor
      return;
    }

    if (this.props?.content.link?.action) {
      this.elementLinkService.handleLinkTrigger(this.props?.content.link);
    } else if (!this.isReadOnly && !this.isStaticImageSource) {
      this.fileInputRef.nativeElement.click();
    }
  }

  fileSelected(fileInput: EventTarget | null): void {
    const fileInputElement = fileInput as HTMLInputElement;
    const files: File[] = fileInputElement.files ? Array.from(fileInputElement.files) : [];
    fileInputElement.value = '';

    if (!files || !files.length) {
      return;
    }
    this.upload(files);
  }

  getEmptyImageUrl(emptyImage: ImageEmptyThumbnail | undefined): string {
    return emptyImage ? EMPTY_ICON_MAP[emptyImage] : '';
  }

  reset(): void {
    this.outputEmitTracker.reset();
    this.wasEmitted = false;
    this.updateOutput(this.value || '');
  }

  download(imgIndex: number): void {
    this.fileService.downloadFile(this.imageUrls[imgIndex]);
  }

  deleteImg(imgIndex: number): void {
    if (!this.imageUrls.length) {
      return;
    }
    const newImageUrls: string[] = this.imageUrls.filter((_, index) => index !== imgIndex);
    this.value = newImageUrls;
    this.updateOutput(this.value);
    if (!this.value.length) {
      this.lastImageSelected = undefined;
    }
    safeDetectChanges(this.cdr);
  }

  expand(imgIndex: number): void {
    const dialogImageRef = this.dialog.open(ImageDialogComponent, {
      width: '100%',
      height: '100%',
      hasBackdrop: true,
      panelClass: 'image-preview-dialog',
      data: {
        src: this.imageUrls,
        startIndex: imgIndex,
        showDeleteButton: !(this.isReadOnly || this.isStaticImageSource),
      },
    });
    dialogImageRef.afterClosed().subscribe((res) => {
      if (res && 'delete' in res) {
        this.deleteImg(res.delete);
      }
    });
    dialogImageRef.componentInstance.downloadImageEvent?.subscribe(($event) => {
      this.fileService.downloadFile($event);
    });
  }

  private initOutputSchema(): void {
    this.dataValue.column$
      .pipe(untilDestroyed(this))
      .subscribe((column: DatasetColumn | undefined) => {
        const multipleValues = column?.multipleValues;
        if (multipleValues) {
          const columns: DatasetColumn[] = [
            {
              columnType: 'Array',
              dataType: 'Array',
              dataTypeDef: {
                columns: [
                  {
                    columnType: 'String',
                    dataType: 'String',
                    isProfilePicture: false,
                    name: '',
                  },
                ],
                name: '',
              },
              isProfilePicture: false,
              name: '',
            },
          ];
          const schema = this.createElementSchema('change', columns);
          this.setDefaultEventSchema('change', schema);
        } else {
          this.setDefaultEventSchema('change');
        }
      });
  }

  private async upload(preselectFiles?: File[]): Promise<void> {
    const options: UploaderOptions = {
      accountId: this.blazeAccountFacade.getAccountId(),
      preselectFiles,
      acceptedFileTypes: this.getSupportedFileTypes(),
      isMultipleSelection: this.uploadLimit !== 1,
    };
    if (this.allowEdit && this.uploadLimit === 1) {
      options.forceCrop = true;
      if (this.forceRatio && this.forceRatio !== 'none') {
        options.cropForceAspectRatio = toRatio(
          this.forceRatio !== 'custom' ? this.forceRatio : this.forceRatioCustom,
        );
      }
      if (this.forceMaxWidth || this.forceMaxHeight) {
        const [maxW, maxH] = [
          parseInt(this.forceMaxWidth ?? '0', 10),
          parseInt(this.forceMaxHeight ?? '0', 10),
        ];
        options.cropMaximumImageSize = { width: maxW, height: maxH };
      }
    }

    this.uploader.upload(options).subscribe((result) => {
      if (!result) {
        return;
      }

      const newImages = result
        .map((res) => res.url)
        .filter((url): url is string => typeof url === 'string');

      this.lastImageSelected = result[result.length - 1] || undefined;
      this.updateOutput(newImages, true);
    });
  }

  private getSupportedFileTypes(): readonly string[] {
    return this.uploader.supportedImageTypes;
  }

  private updateOutput(data: string | string[], isNewValue = false): void {
    const valueColumn = this.getDataInput('value').column;
    if (isNewValue && (typeof data === 'string' || Array.isArray(data))) {
      if (valueColumn?.multipleValues && Array.isArray(this.value)) {
        this.value = [...this.value, ...(Array.isArray(data) ? data : [data])];
      } else {
        this.value = Array.isArray(data) ? [...data] : [data];
      }
    }
    let dataToEmit;
    if (isNewValue) {
      if (valueColumn?.multipleValues) {
        dataToEmit = Array.isArray(this.value) ? this.value : [this.value];
      } else {
        dataToEmit = Array.isArray(this.value) ? this.value?.[0] : this.value;
      }
    } else if (Array.isArray(data) && !data.length) {
      if (valueColumn?.multipleValues) {
        dataToEmit = [];
      } else {
        dataToEmit = null;
      }
    } else {
      dataToEmit = data;
    }
    if (this.outputEmitTracker.shouldEmit(dataToEmit)) {
      this.output.emit({
        eventType: 'change',
        data: dataToEmit,
        isInitialData: !this.wasEmitted,
        schema: this.getEventSchema('change'),
      });
      this.wasEmitted = true;
    }
    this.cdr.detectChanges();
  }

  private updateStyles(): void {
    const { style, content } = this.props || {};
    const { title, options } = content || {};
    const { general, outline, image, labelBlock } = style || {};
    if (!style || !general || !outline || !title || !image || !options || !labelBlock) {
      return;
    }

    const sectionStyles: Record<string, string> = {};
    let imageUploadZoneStyles: Record<string, string> = {
      borderColor: outline.color.color1,
    };
    const imagePlaceholderStyles: Record<string, string> = {};
    const imagePaginationStyles: Record<string, string> = {};
    const imageStyles: Record<string, string> = {
      objectFit: options?.behavior,
      ...makeBorderStyles(outline.color, outline.width),
      ...makeContainerVisualEffectStyles(image.visualEffect),
    };
    if (options.behavior === 'cover') {
      imageStyles['objectFit'] = options.behavior;
      imageStyles['width'] = '100%';
      // necessary for PDF printing
      if (this.elementHeightValue != null) {
        imageUploadZoneStyles['--image-cover-height'] = `${this.elementHeightValue}px`;
      }
    } else if (options.behavior === 'contain') {
      imageStyles['maxWidth'] = '100%';
      // Use max height minus 2 * outline width so there's no overflow/scroll
      imageStyles['maxHeight'] = `calc(100% - 2 * ${outline.width})`;
    }
    const validationStyles: Record<string, string> = {};

    if (title.position === 'top') {
      sectionStyles['flex-direction'] = 'column';
      if (this.elementHeightFixed) {
        imagePaginationStyles['position'] = 'absolute';
        imagePaginationStyles['bottom'] = '0';
        imageUploadZoneStyles['position'] = 'absolute';
        if (options.behavior === 'cover' && this.imageUrls?.length) {
          // 8px = gap, 4px = dot's height
          imageUploadZoneStyles['height'] = `calc(100% - 8px - 4px)`;
        }
      }
    } else {
      validationStyles['margin-left'] = title.width;
      validationStyles['padding-left'] = '8px';

      if (content?.title.enabled) {
        imagePlaceholderStyles['flex-basis'] =
          `${Math.max(100 - toInt(content?.title?.width, 100), 0)}%`;
      }
    }

    if (outline.radius !== '0px') {
      imageStyles['border-radius'] = outline.radius;
      imageUploadZoneStyles['border-radius'] = outline.radius;
    }

    if (options.behavior === 'contain') {
      this.currentImageDisplayIndex = -1;
      imageUploadZoneStyles = {
        ...imageUploadZoneStyles,
        ...makeImageAlignmentStyles(image.offset.align),
      };
    } else {
      this.currentImageDisplayIndex = 0;
      imageStyles['flex-shrink'] = '0';
    }
    if (!this.isStaticImageSource) {
      imageUploadZoneStyles['overflow'] = 'auto';
    }
    if (Array.isArray(this.value)) {
      imageStyles['overflow'] = 'auto clip';
    }

    if (this.hasCustomPlaceholder) {
      const placeholderWidth = content?.imageSource?.placeholder?.width;
      const placeholderHeight = content?.imageSource?.placeholder?.height;

      if (placeholderWidth) {
        imageUploadZoneStyles.width = placeholderWidth;
      }

      if (placeholderHeight) {
        imageUploadZoneStyles.height = placeholderHeight;
      }
    }

    this.imagePlaceholderStyles = imagePlaceholderStyles;
    this.imageUploadZoneStyles = imageUploadZoneStyles;
    this.imagePaginationStyles = imagePaginationStyles;
    this.sectionStyles = sectionStyles;
    this.imageStyles = imageStyles;
    this.validationStyles = validationStyles;
    safeDetectChanges(this.cdr);
  }

  private updateValueField(): void {
    const binding = getActiveBinding(this.props?.data);
    const source = binding?.dataSource;
    if (binding?.dataSource?.sourceType === 'formContainer' || !source) {
      return;
    }
    const schema = this.schemaService.getSchema(source.id, source.sourceType === 'context');
    const valueFieldId = binding?.bindings?.value;
    this.valueField = schema?.columns.find((column) => getColumnKey(column) === valueFieldId)?.name;
  }
}
